/**
 * Lateral steer input: keyboard + pointer/touch drag.
 * Output is sticky in [-1, 1] until released.
 */

export interface LateralInputOptions {
  /** Element that receives pointer events (canvas or full screen). */
  readonly target: HTMLElement;
  /** Keyboard: A/D and arrows. Default true. */
  readonly keyboard?: boolean;
  /** Pointer / touch drag. Default true. */
  readonly pointer?: boolean;
  /** Horizontal pixels of drag that map to full ±1. */
  readonly dragPixelsForFull?: number;
}

/**
 * Collects lateral intent for the sim. Call `dispose()` on teardown.
 */
export class LateralInput {
  private lateral = 0;
  private keyLeft = false;
  private keyRight = false;
  private pointerActive = false;
  private pointerOriginX = 0;
  private pointerLateral = 0;
  private readonly dragPixels: number;
  private readonly target: HTMLElement;
  private readonly useKeyboard: boolean;
  private readonly usePointer: boolean;

  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onPointerDown: (e: PointerEvent) => void;
  private readonly onPointerMove: (e: PointerEvent) => void;
  private readonly onPointerUp: (e: PointerEvent) => void;

  constructor(options: LateralInputOptions) {
    this.target = options.target;
    this.useKeyboard = options.keyboard !== false;
    this.usePointer = options.pointer !== false;
    this.dragPixels = options.dragPixelsForFull ?? 120;

    this.onKeyDown = (e: KeyboardEvent) => {
      if (!this.useKeyboard) return;
      if (e.code === "ArrowLeft" || e.code === "KeyA") {
        this.keyLeft = true;
        e.preventDefault();
      }
      if (e.code === "ArrowRight" || e.code === "KeyD") {
        this.keyRight = true;
        e.preventDefault();
      }
      this.recompute();
    };
    this.onKeyUp = (e: KeyboardEvent) => {
      if (!this.useKeyboard) return;
      if (e.code === "ArrowLeft" || e.code === "KeyA") this.keyLeft = false;
      if (e.code === "ArrowRight" || e.code === "KeyD") this.keyRight = false;
      this.recompute();
    };
    this.onPointerDown = (e: PointerEvent) => {
      if (!this.usePointer) return;
      this.pointerActive = true;
      this.pointerOriginX = e.clientX;
      this.pointerLateral = 0;
      try {
        this.target.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      this.recompute();
    };
    this.onPointerMove = (e: PointerEvent) => {
      if (!this.usePointer || !this.pointerActive) return;
      const dx = e.clientX - this.pointerOriginX;
      this.pointerLateral = Math.max(
        -1,
        Math.min(1, dx / this.dragPixels),
      );
      this.recompute();
    };
    this.onPointerUp = (e: PointerEvent) => {
      if (!this.usePointer) return;
      this.pointerActive = false;
      this.pointerLateral = 0;
      try {
        this.target.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      this.recompute();
    };

    if (this.useKeyboard && typeof window !== "undefined") {
      window.addEventListener("keydown", this.onKeyDown);
      window.addEventListener("keyup", this.onKeyUp);
    }
    if (this.usePointer) {
      this.target.addEventListener("pointerdown", this.onPointerDown);
      this.target.addEventListener("pointermove", this.onPointerMove);
      this.target.addEventListener("pointerup", this.onPointerUp);
      this.target.addEventListener("pointercancel", this.onPointerUp);
      this.target.style.touchAction = "none";
    }
  }

  private recompute(): void {
    let v = 0;
    if (this.keyLeft) v -= 1;
    if (this.keyRight) v += 1;
    if (this.pointerActive) {
      // Pointer overrides keyboard when active
      v = this.pointerLateral;
    }
    this.lateral = Math.max(-1, Math.min(1, v));
  }

  /** Current lateral intent in [-1, 1]. */
  getLateral(): number {
    return this.lateral;
  }

  dispose(): void {
    if (typeof window !== "undefined") {
      window.removeEventListener("keydown", this.onKeyDown);
      window.removeEventListener("keyup", this.onKeyUp);
    }
    this.target.removeEventListener("pointerdown", this.onPointerDown);
    this.target.removeEventListener("pointermove", this.onPointerMove);
    this.target.removeEventListener("pointerup", this.onPointerUp);
    this.target.removeEventListener("pointercancel", this.onPointerUp);
  }
}
